import React, { useState, useEffect, useRef } from 'react';
import { X, Save, Loader2, Camera, Trash2, User as UserIcon } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const copy = {
  fr: {
    title: "Modifier mon profil",
    firstName: "Prénom",
    lastName: "Nom",
    email: "Adresse courriel",
    phone: "Téléphone",
    cancel: "Annuler",
    save: "Enregistrer",
    saving: "Enregistrement…",
    success: "Profil mis à jour avec succès.",
    genericError: "Une erreur s'est produite. Veuillez réessayer.",
    photo: "Photo de profil",
    photoHint: "JPG, PNG ou WebP · max 5 Mo · apparaîtra sur votre badge",
    uploadPhoto: "Téléverser une photo",
    uploading: "Téléversement…",
    removePhoto: "Retirer",
    removing: "Suppression…",
    photoTooBig: "La photo doit faire 5 Mo ou moins.",
    photoWrongType: "Formats acceptés : JPG, PNG, WebP.",
  },
  en: {
    title: "Edit my profile",
    firstName: "First Name",
    lastName: "Last Name",
    email: "Email Address",
    phone: "Phone",
    cancel: "Cancel",
    save: "Save Changes",
    saving: "Saving…",
    success: "Profile updated successfully.",
    genericError: "Something went wrong. Please try again.",
    photo: "Profile Photo",
    photoHint: "JPG, PNG or WebP · max 5 MB · will appear on your badge",
    uploadPhoto: "Upload photo",
    uploading: "Uploading…",
    removePhoto: "Remove",
    removing: "Removing…",
    photoTooBig: "Photo must be 5MB or smaller.",
    photoWrongType: "Accepted formats: JPG, PNG, WebP.",
  },
};

export default function EditProfileModal({ open, onClose, user, token, language = 'fr', onSaved }) {
  const t = copy[language] || copy.fr;
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [photoUrl, setPhotoUrl] = useState('');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [removingPhoto, setRemovingPhoto] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (open && user) {
      setForm({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        phone: user.phone || '',
      });
      setPhotoUrl(user.photo_url || '');
      setError('');
      setSuccess('');
    }
  }, [open, user]);

  if (!open) return null;

  const handleChange = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handlePhotoSelect = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type)) {
      setError(t.photoWrongType);
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError(t.photoTooBig);
      return;
    }
    setUploadingPhoto(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${BACKEND_URL}/api/users/me/photo`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPhotoUrl(res.data?.photo_url || '');
      if (onSaved) onSaved(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || t.genericError);
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleRemovePhoto = async () => {
    setRemovingPhoto(true);
    setError('');
    try {
      const res = await axios.delete(`${BACKEND_URL}/api/users/me/photo`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPhotoUrl('');
      if (onSaved) onSaved(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || t.genericError);
    } finally {
      setRemovingPhoto(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const res = await axios.patch(
        `${BACKEND_URL}/api/users/me`,
        form,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess(t.success);
      if (onSaved) onSaved(res.data);
      setTimeout(() => onClose(), 1200);
    } catch (err) {
      setError(err?.response?.data?.detail || t.genericError);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="edit-profile-modal"
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-extrabold text-[#1D1D1D]">{t.title}</h2>
          <button
            type="button"
            onClick={onClose}
            data-testid="edit-profile-close"
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4" data-testid="edit-profile-form">
          {/* Profile Photo */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-2">{t.photo}</label>
            <div className="flex items-center gap-4">
              <div className="relative">
                {photoUrl ? (
                  <img
                    src={photoUrl.startsWith('http') ? photoUrl : `${BACKEND_URL}${photoUrl}`}
                    alt="Profile"
                    data-testid="edit-profile-photo-preview"
                    className="w-20 h-20 rounded-full object-cover border-2 border-[#CD0000]/30 shadow-sm"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#CD0000]/10 to-[#D4AF37]/10 border-2 border-dashed border-gray-200 flex items-center justify-center">
                    <UserIcon className="w-8 h-8 text-gray-300" />
                  </div>
                )}
                {uploadingPhoto && (
                  <div className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center">
                    <Loader2 className="w-6 h-6 text-white animate-spin" />
                  </div>
                )}
              </div>
              <div className="flex-1">
                <div className="flex gap-2 flex-wrap">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handlePhotoSelect}
                    className="hidden"
                    data-testid="edit-profile-photo-input"
                  />
                  <button
                    type="button"
                    disabled={uploadingPhoto || removingPhoto}
                    onClick={() => fileInputRef.current?.click()}
                    data-testid="edit-profile-photo-upload"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#CD0000]/10 hover:bg-[#CD0000]/15 text-[#CD0000] text-xs font-bold transition disabled:opacity-60"
                  >
                    <Camera className="w-3.5 h-3.5" />
                    {uploadingPhoto ? t.uploading : t.uploadPhoto}
                  </button>
                  {photoUrl && !uploadingPhoto && (
                    <button
                      type="button"
                      disabled={removingPhoto}
                      onClick={handleRemovePhoto}
                      data-testid="edit-profile-photo-remove"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-600 text-xs font-bold transition disabled:opacity-60"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      {removingPhoto ? t.removing : t.removePhoto}
                    </button>
                  )}
                </div>
                <p className="text-[11px] text-gray-400 mt-1.5 leading-snug">{t.photoHint}</p>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1.5">{t.firstName}</label>
            <input
              data-testid="edit-profile-first-name"
              type="text"
              required
              value={form.first_name}
              onChange={handleChange('first_name')}
              className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#CD0000]"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1.5">{t.lastName}</label>
            <input
              data-testid="edit-profile-last-name"
              type="text"
              required
              value={form.last_name}
              onChange={handleChange('last_name')}
              className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#CD0000]"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1.5">{t.email}</label>
            <input
              data-testid="edit-profile-email"
              type="email"
              required
              value={form.email}
              onChange={handleChange('email')}
              className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#CD0000]"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1.5">{t.phone}</label>
            <input
              data-testid="edit-profile-phone"
              type="tel"
              value={form.phone}
              onChange={handleChange('phone')}
              className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#CD0000]"
            />
          </div>

          {error && (
            <p data-testid="edit-profile-error" className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</p>
          )}
          {success && (
            <p data-testid="edit-profile-success" className="text-sm text-green-700 bg-green-50 border border-green-100 rounded-lg px-3 py-2">{success}</p>
          )}

          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              data-testid="edit-profile-cancel"
              className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-2.5 rounded-xl transition"
            >
              {t.cancel}
            </button>
            <button
              type="submit"
              disabled={saving}
              data-testid="edit-profile-submit"
              className="flex-1 bg-[#CD0000] hover:bg-[#A00000] disabled:opacity-60 text-white font-semibold py-2.5 rounded-xl transition flex items-center justify-center gap-2"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {saving ? t.saving : t.save}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
